import React, { useContext, useEffect, useState } from 'react';
import { Table } from 'reactstrap';
import ReactPaginate from 'react-paginate';
import { format } from 'date-fns';
import { ProductContext } from '../../../../store';
import ReportHeader from '../../../../components/Search/ReportHeader';
import ReportSortArrows from '../../../../components/Search/ReportSortArrows';
import DetailSkeleton from '../../../../components/Skeleton/DetailSkeleton';
import TableSkeleton from '../../../../components/Skeleton/TableSkeleton';
import { PostData } from '../../../../services/FetchData';
import { searchReportsAnalyticsUrl } from '../../../../constants/searchReportsAnalyticsApi';
import { toast } from 'react-toastify';

const ProductKeywords = () => {
    const [pageLoader, setPageLoader] = useState(true),
        [tableLoader, setTableLoader] = useState(true),
        [products, setProducts] = useState([]),
        [limit, setLimit] = useState(10),
        [page, setPage] = useState(0),
        [totalPages, setTotalPages] = useState(0),
        [sortBy, setSortBy] = useState("conversions"),
        [sortOrder, setSortOrder] = useState("desc"), // "asc" | "desc"
        [expandedProducts, setExpandedProducts] = useState({}),
        { dateRange, setDateRange, currentWebsiteId = 1 } = useContext(ProductContext);

    const toggleSort = (field, e) => {
        e?.stopPropagation?.();
        setPage(0);
        setSortBy(prevField => {
            // If switching to a new field, default to desc
            if (prevField !== field) {
                setSortOrder("desc");
                return field;
            }
            // If clicking same field, toggle direction
            setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
            return prevField;
        });
    };

    const setSort = (field, order, e) => {
        e?.stopPropagation?.();
        setPage(0);
        setSortBy(field);
        setSortOrder(order);
    };

    useEffect(() => {
        if (currentWebsiteId) {
            // Reset expanded states when page or limit changes
            setExpandedProducts({});
            getReports();
        }
    }, [limit, page, currentWebsiteId, dateRange, sortBy, sortOrder]);

    const getReports = () => {
        setTableLoader(true);
        let websiteId = currentWebsiteId,
            payload = {
                websiteId,
                startDate: format(dateRange.startDate, "yyyy-MM-dd"),
                endDate: format(dateRange.endDate, "yyyy-MM-dd"),
                page: page + 1,
                limit,
                sortBy,
                sortOrder,
            };
        
        PostData("ms1", searchReportsAnalyticsUrl("productSearchKeyPerformanceGrouped"), payload).then(response => {
            if (response !== "Invalid" && response !== "undefined") {
                if (response.status === "success") {
                    const raw = response.data?.data || [];
                    const normalized = Array.isArray(raw) ? raw.map((p) => ({ ...p, keywords: Array.isArray(p?.keywords) ? p.keywords : [] })) : [];
                    setProducts(normalized);
                    setTotalPages(response.data.totalPages || 0);
                } else {
                    toast.error(response.message || "Failed to fetch product keywords data");
                }
            }
            setTableLoader(false);
            setPageLoader(false);
        }).catch(err => {
            console.error("Error fetching product keywords:", err);
            toast.error("Error fetching product keywords data");
            setTableLoader(false);
            setPageLoader(false);
        });
    };

    const toggleProductExpansion = (productId) => {
        setExpandedProducts(prev => ({
            ...prev,
            [productId]: !prev[productId]
        }));
    };

    const getTotalMetrics = (keywords) => {
        return keywords.reduce((acc, keyword) => {
            acc.impressions += keyword.impressions || 0;
            acc.clicks += keyword.clicks || 0;
            acc.addToCarts += keyword.addToCarts || 0;
            acc.conversions += keyword.conversions || 0;
            acc.revenue += keyword.revenue || 0;
            return acc;
        }, { impressions: 0, clicks: 0, addToCarts: 0, conversions: 0, revenue: 0 });
    };

    const safeInt = (value) => (typeof value === "number" && Number.isFinite(value) ? value : Number(value) || 0);

    const getKeywordSearchVolume = (keyword) => {
        const hasDirectVolume =
            keyword?.searchVolume !== undefined && keyword?.searchVolume !== null && keyword?.searchVolume !== "";
        const directVolume = safeInt(keyword?.searchVolume);
        return hasDirectVolume ? directVolume : 0;
    };

    // CTR% as requested: conversions / impressions (not clicks)
    const calculateTotalCTR = (impressions, conversions) => {
        return impressions > 0 ? ((conversions / impressions) * 100).toFixed(2) : "0.00";
    };

    const calculateTotalConversionRate = (impressions, conversions) => {
        return impressions > 0 ? ((conversions / impressions) * 100).toFixed(2) : "0.00";
    };

    return (
        <div>
            {pageLoader ?
                <DetailSkeleton />
            :
                <React.Fragment>
                    <ReportHeader range={dateRange} onDateChange={(range) => setDateRange(range)} reportKey="productKeywords" sortBy={sortBy} sortOrder={sortOrder} />
                    <div className="tab-content mt-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="mb-0">Product Keyword Performance</h4>
                            <div className="d-flex align-items-center">
                                <label htmlFor="limit" className="me-2">
                                    Items Per Page:
                                </label>
                                <select 
                                    id="limit" 
                                    className="form-select" 
                                    value={limit} 
                                    onChange={(e) => {
                                        setLimit(Number(e.target.value));
                                        setPage(0);
                                    }} 
                                    style={{ width: "100px", fontSize: "12px" }}
                                >
                                    <option value={25}>25</option>
                                    <option value={30}>30</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            {tableLoader ?
                                <TableSkeleton />
                            :
                                <Table className={`table ${products.length > 0 ? "table-striped" : ""} mt-3 custom-bordered-table`}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: "30px" }}></th>
                                            <th>Product</th>
                                            <th className="sortable-header" style={{ cursor: "pointer" }} onClick={(e) => toggleSort("impressions", e)}>
                                                <div className="sortable-header-inner">
                                                    Impressions
                                                    <ReportSortArrows field="impressions" activeSortField={sortBy} sortOrder={sortOrder} onSort={setSort} />
                                                </div>
                                            </th>
                                            <th className="sortable-header" style={{ cursor: "pointer" }} onClick={(e) => toggleSort("clicks", e)}>
                                                <div className="sortable-header-inner">
                                                    Clicks
                                                    <ReportSortArrows field="clicks" activeSortField={sortBy} sortOrder={sortOrder} onSort={setSort} />
                                                </div>
                                            </th>
                                            <th className="sortable-header" style={{ cursor: "pointer" }} onClick={(e) => toggleSort("addToCarts", e)}>
                                                <div className="sortable-header-inner">
                                                    Add-to-Cart
                                                    <ReportSortArrows field="addToCarts" activeSortField={sortBy} sortOrder={sortOrder} onSort={setSort} />
                                                </div>
                                            </th>
                                            <th className="sortable-header" style={{ cursor: "pointer" }} onClick={(e) => toggleSort("conversions", e)}>
                                                <div className="sortable-header-inner">
                                                    Conversions
                                                    <ReportSortArrows field="conversions" activeSortField={sortBy} sortOrder={sortOrder} onSort={setSort} />
                                                </div>
                                            </th>
                                            <th className="sortable-header" style={{ cursor: "pointer" }} onClick={(e) => toggleSort("revenue", e)}>
                                                <div className="sortable-header-inner">
                                                    Revenue
                                                    <ReportSortArrows field="revenue" activeSortField={sortBy} sortOrder={sortOrder} onSort={setSort} />
                                                </div>
                                            </th>
                                            <th className="sortable-header" style={{ cursor: "pointer" }} onClick={(e) => toggleSort("ctr", e)}>
                                                <div className="sortable-header-inner">
                                                    CTR %
                                                    <ReportSortArrows field="ctr" activeSortField={sortBy} sortOrder={sortOrder} onSort={setSort} />
                                                </div>
                                            </th>
                                            <th className="sortable-header" style={{ cursor: "pointer" }} onClick={(e) => toggleSort("conversionRate", e)}>
                                                <div className="sortable-header-inner">
                                                    Conversion Rate %
                                                    <ReportSortArrows field="conversionRate" activeSortField={sortBy} sortOrder={sortOrder} onSort={setSort} />
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {products.length === 0 ? (
                                            <tr>
                                                <td colSpan={9}>
                                                    <div className="emptyPlaceholder">
                                                        <div>
                                                            <div>
                                                                <img 
                                                                    src={require("./../../../../assets/images/paper-plane-grey.png")} 
                                                                    className='img-fluid' 
                                                                    alt="No data found" 
                                                                />
                                                            </div>
                                                            <div className="rsInfoMsg disabled">
                                                                <div className="flex-1">No records found.</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            products.map((product, productIndex) => {
                                                const isExpanded = expandedProducts[product.productId];
                                                const keywords = product.keywords || [];
                                                const totalMetrics = getTotalMetrics(keywords);
                                                const rowImpressions = product.impressions ?? totalMetrics.impressions;
                                                const rowClicks = product.clicks ?? totalMetrics.clicks;
                                                const rowAddToCarts = product.addToCarts ?? totalMetrics.addToCarts;
                                                const rowConversions = product.conversions ?? totalMetrics.conversions;
                                                const rowRevenue = product.revenue ?? totalMetrics.revenue;
                                                const totalCTR = calculateTotalCTR(rowImpressions, rowConversions);
                                                const totalConversionRate = calculateTotalConversionRate(rowImpressions, rowConversions);

                                                return (
                                                    <React.Fragment key={product.productId}>
                                                        <tr 
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => toggleProductExpansion(product.productId)}
                                                        >
                                                            <td>
                                                                <i 
                                                                    className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}
                                                                    style={{ 
                                                                        fontSize: '12px',
                                                                        color: '#666',
                                                                        transition: 'transform 0.2s'
                                                                    }}
                                                                />
                                                            </td>
                                                            <td>
                                                                <div className='d-flex align-items-center'>
                                                                    <img
                                                                        src={product.productImage || require("./../../../../assets/images/paper-plane-grey.png")}
                                                                        alt={product.productName}
                                                                        width="35"
                                                                        height="35"
                                                                        style={{
                                                                            objectFit: "cover",
                                                                            marginRight: "8px",
                                                                            borderRadius: "4px"
                                                                        }}
                                                                        onError={(e) => {
                                                                            e.target.src = require("./../../../../assets/images/paper-plane-grey.png");
                                                                        }}
                                                                    />
                                                                    <span>{product.productName || 'N/A'}</span>
                                                                </div>
                                                            </td>
                                                            <td>{rowImpressions.toLocaleString()}</td>
                                                            <td>{rowClicks.toLocaleString()}</td>
                                                            <td>{rowAddToCarts.toLocaleString()}</td>
                                                            <td>{rowConversions.toLocaleString()}</td>
                                                            <td>${typeof rowRevenue === 'number' ? rowRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : rowRevenue}</td>
                                                            <td>{totalCTR}%</td>
                                                            <td>{totalConversionRate}%</td>
                                                        </tr>
                                                        {isExpanded && (
                                                            <tr>
                                                                <td colSpan={9} style={{ padding: 0, backgroundColor: '#f8f9fa' }}>
                                                                    <div style={{ padding: '15px', marginLeft: '30px' }}>
                                                                        {keywords.length === 0 ? (
                                                                            <div className="text-muted small">No keyword data for this product.</div>
                                                                        ) : (
                                                                            <Table className="table table-sm mb-0" style={{ backgroundColor: 'white' }}>
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th>Search Keyword</th>
                                                                                        <th>Impressions</th>
                                                                                        <th>Search Volume</th>
                                                                                        <th>Clicks</th>
                                                                                        <th>Add-to-Cart</th>
                                                                                        <th>Conversions</th>
                                                                                        <th>Revenue</th>
                                                                                        <th>CTR %</th>
                                                                                        <th>ATC Rate %</th>
                                                                                        <th>Conversion Rate %</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {keywords.map((keyword, keywordIndex) => {
                                                                                        const keywordSearchVolume = getKeywordSearchVolume(keyword);
                                                                                        const keywordImpressions = safeInt(keyword.impressions);
                                                                                        return (
                                                                                            <tr key={keywordIndex}>
                                                                                                <td>
                                                                                                    <span style={{ padding: '4px 8px', backgroundColor: '#e3f2fd', borderRadius: '4px', fontSize: '13px', fontWeight: 500 }}>
                                                                                                        {keyword.searchKey || 'N/A'}
                                                                                                    </span>
                                                                                                </td>
                                                                                                <td>{keywordImpressions.toLocaleString()}</td>
                                                                                                <td>{keywordSearchVolume.toLocaleString()}</td>
                                                                                                <td>{keyword.clicks?.toLocaleString() || 0}</td>
                                                                                                <td>{keyword.addToCarts?.toLocaleString() || 0}</td>
                                                                                                <td>{keyword.conversions?.toLocaleString() || 0}</td>
                                                                                                <td>${keyword.revenue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                                                                                                <td>{calculateTotalCTR(keywordImpressions || keywordSearchVolume, keyword.conversions || 0)}%</td>
                                                                                                <td>{typeof keyword.atcRate === 'number' ? keyword.atcRate.toFixed(2) : keyword.atcRate || '0.00'}%</td>
                                                                                                <td>{typeof keyword.conversionRate === 'number' ? keyword.conversionRate.toFixed(2) : keyword.conversionRate || '0.00'}%</td>
                                                                                            </tr>
                                                                                        );
                                                                                    })}
                                                                                </tbody>
                                                                            </Table>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </Table>
                            }
                            {totalPages > 1 &&
                                <div className='d-flex justify-content-center mt-5'>
                                    <ReactPaginate
                                        nextLabel=">"
                                        onPageChange={(event) => setPage(event.selected)}
                                        page={page}
                                        pageRangeDisplayed={3}
                                        marginPagesDisplayed={2}
                                        pageCount={totalPages}
                                        previousLabel="<"
                                        pageClassName="page-item"
                                        pageLinkClassName="page-link"
                                        previousClassName="page-item"
                                        previousLinkClassName="page-link"
                                        nextClassName="page-item"
                                        nextLinkClassName="page-link"
                                        breakLabel="..."
                                        breakClassName="page-item"
                                        breakLinkClassName="page-link"
                                        containerClassName="pagination"
                                        activeClassName="active"
                                        renderOnZeroPageCount={null}
                                        forcePage={page}
                                    />
                                </div>
                            }
                        </div>
                    </div>
                </React.Fragment>
            }
        </div>
    );
};

export default ProductKeywords;